import { useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import { useBindGeoSheetByCoordsMutation, useGetGeoSheetCoordsQuery } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Hint, Notice, useAction } from '@widgets/GeoSchemes';
import { useWizard } from './wizard';

const Box = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Title = styled.h4`
  margin: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/** Чем закончилась привязка: взятые и не сошедшиеся выноски. */
interface Done {
  used: number;
  total: number;
  rejected: string[];
}

/**
 * Привязка по выноскам координат на самом листе (шаг 2): на схемах посадки у
 * углов конструкций стоят «X=…, Y=…» со стрелкой — программа находит их и
 * привязывает лист без ввода (перебор пар, МНК, опечатка отбрасывается). При
 * загрузке листа с тремя и больше сошедшимися выносками это происходит само;
 * кнопка — чтобы привязать заново. Выносок на листе нет — панели нет.
 */
export function CoordsBindPanel({
  draftPoints,
  dirty,
  onDone,
}: {
  /** Сколько точек привязки сейчас в черновике шага — их заменят выноски. */
  draftPoints: number;
  /** Есть несохранённые правки привязки. */
  dirty: boolean;
  /** Сбросить черновик шага — он подхватит точки с сервера. */
  onDone: () => void;
}) {
  const { scheme, readOnly } = useWizard();
  const sheet = scheme.sheet;
  const skip = readOnly || sheet?.kind !== 'pdf';
  const { data } = useGetGeoSheetCoordsQuery(
    { id: scheme.id, fileId: sheet?.file_id ?? '', page: sheet?.page ?? 0 },
    { skip },
  );
  const [bind] = useBindGeoSheetByCoordsMutation();
  const binding = useAction();
  const [done, setDone] = useState<Done | null>(null);
  /** Ответ сервера (привязка в JSON), который ещё не дошёл до карточки в кеше. */
  const [pending, setPending] = useState<{ binding: string; done: Done } | null>(null);
  const bindingKey = JSON.stringify(scheme.settings.binding);

  useLayoutEffect(() => {
    if (!pending || pending.binding !== bindingKey) return;
    onDone();
    setDone(pending.done);
    setPending(null);
  }, [pending, bindingKey, onDone]);

  const callouts = data?.callouts ?? [];
  if (skip || callouts.length < 2) return null;

  const fromCallouts = scheme.settings.binding.control_points.filter((c) =>
    callouts.some((k) => `auto:${k.label}` === c.id),
  ).length;

  const run = async () => {
    if (
      draftPoints > 0 &&
      !window.confirm(
        dirty
          ? 'Несохранённые точки привязки заменятся выносками с листа. Продолжить?'
          : 'Текущие точки привязки заменятся выносками с листа. Продолжить?',
      )
    )
      return;
    setDone(null);
    setPending(null);
    const res = await binding.runValue(
      () => bind(scheme.id).unwrap(),
      'Привязка по выноскам не выполнена.',
    );
    if (!res) return;
    const used = res.settings.binding.control_points.map((c) => c.id.replace(/^auto:/, ''));
    setPending({
      binding: JSON.stringify(res.settings.binding),
      done: {
        used: used.length,
        total: callouts.length,
        rejected: callouts
          .filter((k) => !used.includes(k.label))
          .map((k) => `${k.label} (${k.text})`),
      },
    });
  };

  return (
    <Box aria-labelledby="geo-coordsbind-title">
      <Head>
        <Title id="geo-coordsbind-title">Привязка по координатам на листе</Title>
        <Button
          type="button"
          $variant="secondary"
          $size="sm"
          onClick={() => void run()}
          disabled={binding.busy}
        >
          {binding.busy ? 'Привязываем…' : 'Привязать по выноскам'}
        </Button>
      </Head>
      <Hint>
        На листе выносок с координатами площадки («X=…, Y=…» со стрелкой): {callouts.length}. По ним
        лист ложится в координаты без ввода — опечатку в одной выноске программа отбросит.
        {fromCallouts > 0 ? ` Сейчас лист привязан по ним (точек: ${fromCallouts}).` : ''}
      </Hint>
      {binding.error && (
        <Notice $tone="danger" role="alert">
          {binding.error}
        </Notice>
      )}
      {done && !binding.error && (
        <Notice $tone="success" role="status">
          Лист привязан по выноскам: взяты {done.used} из {done.total}
          {done.rejected.length > 0 ? `; не сошлись — ${done.rejected.join(', ')}` : ''}. Невязки —
          ниже.
        </Notice>
      )}
    </Box>
  );
}
